// apps/client/src/ui/screens/loan.tsx (M1-T14) — the home loan, at the shop's loan desk or the 24-hour machine by the drop box
// (params { at }). Pay any amount (no interest, spec §9.4); once nothing is owed, ask for the next house size: the tent → cottage step
// picks a roof colour first (the `roof` screen), every later step goes straight to the builders, who come at tuning.time.constructionHour.
// Two columns (M1 playtest, 844 x 390: the panel scrolled, Pay below the fold and the bubble sliding under the title tab, while its
// right half stood empty): Bramble, your house and what is owed on the left; your bells, quick-pay, the amount and Pay on the right.
import { tuning, type HouseLevel, type StringKey } from '@whiskerton/content';
import { upgradeCost } from '@whiskerton/shared/rules/house';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { getGame, useCommands } from '../../game/instance';
import { payLoan, requestUpgrade } from '../../state/slices/shop';
import { useGame } from '../../state/store';
import { useT } from '../i18n';
import { Bubble, Button, Panel, TextField } from '../kit';
import type { ScreenDef } from '../router';
import { injectShopStyle } from './sell';
import { hourText } from './shop';

declare module '../router' { interface ScreenParams { loan: { at: 'shop' | 'machine' } } }

const levelName = (l: number) => `house.level${l}` as StringKey;

function Loan({ at, close }: { at: 'shop' | 'machine'; close: () => void }) {
  injectShopStyle();
  const t = useT();
  const cmd = useCommands();
  const { you, me, space, openScreen } = useGame(useShallow((s) => ({ you: s.you, me: s.you ? s.residents[s.you.resident] : undefined, space: s.space, openScreen: s.openScreen })));
  const [amount, setAmount] = useState('');
  const [line, setLine] = useState<StringKey>('shop.loanHello');
  const [busy, setBusy] = useState(false);
  const here = at === 'shop' ? space === 'shop' : space === 'out';
  useEffect(() => { if (!here) close(); }, [here, close]);
  if (!you) return null;
  const level = (me?.houseLevel ?? 0) as HouseLevel, pending = me?.pendingUpgrade ?? null;
  const max = Math.min(you.loan, you.bells);
  const typed = Number(amount), valid = /^\d+$/.test(amount) && typed >= 1 && typed <= max;
  const quickPay = tuning.shop.loanQuickPay.filter((q) => q <= you.loan);   // never more than is still owed (M1 playtest)
  const pay = (n: number) => {
    setBusy(true);
    void payLoan(cmd.act, n).then((r) => {
      setBusy(false);
      if (!r.ok) return;
      setAmount('');
      setLine(r.r.paidOff ? 'shop.paidOff' : 'shop.paid');
      getGame().sound?.sfx('coin');
    });
  };
  const upgrade = () => {
    if (level === 0) { openScreen('roof', undefined); return; }          // the cottage needs a roof colour first
    setBusy(true);
    void requestUpgrade(cmd.act, null).then((r) => { setBusy(false); if (r.ok) setLine('shop.upgraded'); });
  };
  const hour = hourText(tuning.time.constructionHour);
  return (
    <Panel title={t('shop.loanTitle')} className="shop-panel">
      <div className="loan-layout">
        <div className="loan-talk">
          {at === 'shop' && <Bubble name={t('shop.shopkeeperName')}>{t(line, { hour })}</Bubble>}
          <p className="shop-note">{t('shop.yourHouse', { level: t(levelName(level)) })}</p>
          <p className="sell-total">{t(you.loan > 0 ? 'shop.loanOwed' : 'shop.loanClear', { loan: you.loan })}</p>
        </div>
        {you.loan > 0 ? (
          <div className="loan-side loan-pay">
            <p className="shop-note">{t('shop.wallet', { n: you.bells })}</p>
            <div className="loan-quick">
              {quickPay.map((q) => (
                <Button key={q} variant="quiet" disabled={busy || q > max} onClick={() => pay(q)}>{t('shop.price', { price: q })}</Button>
              ))}
              <Button variant="quiet" disabled={busy || max < 1} onClick={() => pay(max)}>{t('shop.payAll')}</Button>
            </div>
            <div className="loan-amount">
              <TextField label={t('shop.amount')} value={amount} inputMode="numeric" maxLength={9} onChange={(v) => setAmount(v.replace(/\D/g, ''))} />
              <Button disabled={busy || !valid} onClick={() => pay(typed)}>{t('shop.payButton', { amount: valid ? typed : 0 })}</Button>
            </div>
          </div>
        ) : (
          <div className="loan-side loan-pay">
            {pending ? <p className="shop-note">{t('shop.upgradePending', { hour })}</p>
              : level < 4 ? <Button disabled={busy} onClick={upgrade}>{t('shop.upgradeTo', { level: t(levelName(level + 1)), cost: upgradeCost((level + 1) as HouseLevel) })}</Button>
                : <p className="shop-note">{t('shop.maxLevel')}</p>}
          </div>
        )}
      </div>
    </Panel>
  );
}
export const screen: ScreenDef<'loan'> = { id: 'loan', layer: 'panel', component: ({ params, close }) => <Loan at={params.at} close={close} /> };
